export function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Outbound URL for a tool. Routes through /api/go/<slug> so the backend
 * can transparently 302 to an affiliate URL when one is set (admin-only,
 * never exposed in the public Tool payload). For tools without a slug
 * (very old / migration edge cases) we fall back to the raw url.
 * Use this anywhere you'd previously write `tool.url` in an outbound
 * `<a href>`.
 *
 * `from` is the surface the click came from (home / tool / concierge /
 * terminal / …); it's passed to /api/go so the backend can attribute the
 * outbound click per section in the admin analytics. `vid` ties the click to
 * the anonymous visitor for unique-visitor counts.
 */
export function outboundHref(tool, from) {
  if (!tool) return "#";
  if (tool.slug) {
    const params = [];
    if (from) params.push(`from=${encodeURIComponent(from)}`);
    try {
      const vid = localStorage.getItem("tv_vid");
      if (vid) params.push(`vid=${encodeURIComponent(vid)}`);
    } catch {
      /* ignore */
    }
    return `/api/go/${tool.slug}${params.length ? `?${params.join("&")}` : ""}`;
  }
  return tool.url || "#";
}

export function unslug(slug, categories) {
  return categories.find((c) => slugify(c) === slug);
}

export function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const yy = String(d.getFullYear()).slice(2);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  } catch {
    return "";
  }
}

export function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, (Date.now() - t) / 1000);
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 86400 * 365) return `${Math.floor(diff / (86400 * 30))}mo ago`;
  return `${Math.floor(diff / (86400 * 365))}y ago`;
}

export function domainOf(url) {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

// Build a short, useful query string for "find similar tools" links from a
// free-form phrase like a key-feature blurb. Strips punctuation/stopwords
// and returns the first 1-2 meaningful tokens (>=4 chars where possible).
const STOPWORDS = new Set([
  "a", "an", "and", "or", "but", "the", "of", "for", "in", "on", "to",
  "with", "by", "from", "at", "as", "is", "are", "be", "this", "that",
  "your", "you", "via", "per", "no", "yes", "any", "all", "into", "onto",
  "over", "under",
]);

export function similarQuery(text) {
  if (!text) return "";
  const tokens = text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/\s+/)
    .filter((t) => t && !STOPWORDS.has(t));
  // Prefer the first two tokens with length >= 4; fall back to whatever's there.
  const long = tokens.filter((t) => t.length >= 4);
  const picks = (long.length >= 2 ? long : tokens).slice(0, 2);
  return picks.join(" ");
}


/**
 * Client-side "find similar tools" — mirrors the backend
 * `/api/tools/{slug}/related` ranking so the home-row reveal returns
 * the same picks as the tool-detail rail. Pure function of the
 * already-loaded tool list; no extra fetch needed.
 *
 * Score: +2 for matching asset_class, +1 for each shared category.
 * Tie-break by upvote count (desc).
 */
export function findSimilar(tool, allTools, n = 3) {
  if (!tool || !Array.isArray(allTools) || allTools.length === 0) return [];
  const myCats = new Set([tool.category, tool.category_2].filter(Boolean));
  const score = (other) => {
    if (!other || other.id === tool.id) return -1;
    let s = 0;
    if (tool.asset_class && other.asset_class === tool.asset_class) s += 2;
    const otherCats = [other.category, other.category_2].filter(Boolean);
    for (const c of otherCats) if (myCats.has(c)) s += 1;
    return s;
  };
  return allTools
    .map((t) => ({ t, s: score(t) }))
    .filter((x) => x.s > 0)
    .sort(
      (a, b) =>
        b.s - a.s || (b.t.upvotes || 0) - (a.t.upvotes || 0),
    )
    .slice(0, n)
    .map((x) => x.t);
}
